import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const ServiceClient = () => {
  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://ziexter.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ziexter.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Service Client",
        "item": "https://ziexter.com/services/service-client"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Service Client externalisé - ZiExter | Call Center multicanal</title>
        <meta
          name="description"
          content="Externalisez votre service client avec ZiExter : accueil téléphonique, support email/chat, gestion réclamations. Multilingue, 24/7, transformez votre relation client."
        />
        <link rel="canonical" href="https://ziexter.com/services/service-client" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdBreadcrumb)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Service Client</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Rejoignez le futur de l'externalisation.
            </h1>
            <p className="text-xl text-gray-100">
              Nous aidons déjà des dizaines de marques à transformer leur service client en un moteur de croissance et de fidélisation !
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card text-center hover-lift animate-fade-in-up">
              <div className="text-4xl font-bold text-primary mb-2">+25%</div>
              <div className="text-sm text-gray-600 mb-4">de CSAT en 4 semaines</div>
              <p className="text-xs text-gray-500">
                Nos clients reçoivent des réponses plus rapides et plus personnalisées. Offrir un service client irréprochable est devenu essentiel pour renforcer l'image de marque et fidéliser durablement.
              </p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-100">
              <div className="text-4xl font-bold text-primary mb-2">+50</div>
              <div className="text-sm text-gray-600 mb-4">agents multilingues intégrés en 6 semaines</div>
              <p className="text-xs text-gray-500">
                CSAT de 4,5/5. Aucun autre prestataire n'aurait pu recruter et former autant d'agents en si peu de temps, avec une telle efficacité.
              </p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-200">
              <div className="text-4xl font-bold text-primary mb-2">&lt;25min</div>
              <div className="text-sm text-gray-600 mb-4">Temps de première réponse email</div>
              <p className="text-xs text-gray-500">
                Service 24/7. Ce qui est précieux avec ZiExter, c'est la transparence : un tableau de bord en temps réel pour suivre les performances, ajuster les process et donner du feedback instantané.
              </p>
            </div>

            <div className="card text-center hover-lift animate-fade-in-up animation-delay-300">
              <div className="flex items-center justify-center mb-4">
                <Link to="/rendez-vous" className="btn btn-primary btn-sm">
                  📌 Prendre rendez-vous
                </Link>
              </div>
              <p className="text-xs text-gray-600">
                Vous êtes à 1 minute de votre meilleure expérience client.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Le problème */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Le problème</h2>
            <p className="text-xl text-gray-700 mb-4">
              Il coûte <strong className="text-primary">9x plus cher</strong> d'acquérir un nouveau client que de fidéliser un client existant.
            </p>
            <p className="text-xl text-gray-700 mb-4">
              👉 Et pourtant, <strong className="text-primary">65% des consommateurs</strong> abandonnent une marque après une mauvaise expérience de support.
            </p>
            <p className="text-2xl font-bold text-primary mt-6">
              Mais ça, c'était avant ZiExter.
            </p>
          </div>
        </div>
      </section>

      {/* Nos chiffres */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos chiffres en service client.</h2>
            <p className="section-subtitle">
              Nous gérons vos interactions avec excellence pour que vous puissiez vous concentrer sur votre cœur de métier.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card hover-lift animate-fade-in-up">
              <div className="text-3xl font-bold text-primary mb-2">2M+</div>
              <p className="text-sm text-gray-600">interactions résolues : email, téléphone, chat, réseaux sociaux</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-50">
              <div className="text-3xl font-bold text-primary mb-2">1h</div>
              <p className="text-sm text-gray-600">de délai moyen sur le premier email</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <div className="text-3xl font-bold text-primary mb-2">20s</div>
              <p className="text-sm text-gray-600">pour une réponse initiale au chat</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-150">
              <div className="text-3xl font-bold text-primary mb-2">95%</div>
              <p className="text-sm text-gray-600">de qualité de service sur appels entrants (moins de 30s d'attente)</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <div className="text-3xl font-bold text-primary mb-2">0,8%</div>
              <p className="text-sm text-gray-600">de taux d'escalade grâce à nos process éprouvés</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-250">
              <div className="text-3xl font-bold text-primary mb-2">24/7</div>
              <p className="text-sm text-gray-600">disponibilité sans interruption</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-300">
              <div className="text-3xl font-bold text-primary mb-2">92%</div>
              <p className="text-sm text-gray-600">de taux d'assurance qualité</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-350">
              <div className="text-3xl font-bold text-primary mb-2">35+</div>
              <p className="text-sm text-gray-600">langues couvertes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre offre */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Notre offre.</h2>
            <p className="section-subtitle">
              Déléguez tout ou seulement une partie : ZiExter s'adapte à vos besoins.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card hover-lift animate-fade-in-up">
              <h3 className="font-semibold mb-2">Recherche d'agents</h3>
              <p className="text-sm text-gray-600">Un recrutement agile, déjà optimisé sur +2K profils onboardés</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-50">
              <h3 className="font-semibold mb-2">Formation des agents</h3>
              <p className="text-sm text-gray-600">Une académie sur mesure</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <h3 className="font-semibold mb-2">Routing intelligent des tickets</h3>
              <p className="text-sm text-gray-600">Chaque demande dirigée vers le bon interlocuteur</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-150">
              <h3 className="font-semibold mb-2">Planification dynamique</h3>
              <p className="text-sm text-gray-600">Allocation d'agents adaptée aux pics d'activité</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <h3 className="font-semibold mb-2">Réponses assistées par IA</h3>
              <p className="text-sm text-gray-600">Des réponses rapides, toujours vérifiées par des humains</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-250">
              <h3 className="font-semibold mb-2">Assurance qualité continue</h3>
              <p className="text-sm text-gray-600">Minimum 5% des tickets analysés et corrigés</p>
            </div>
            <div className="card hover-lift animate-fade-in-up animation-delay-300">
              <h3 className="font-semibold mb-2">Tableau de bord en temps réel</h3>
              <p className="text-sm text-gray-600">Suivi transparent avec KPI clairs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">ZiExter accompagne déjà des équipes ambitieuses.</h2>
            <p className="section-subtitle">
              Startups, PME ou centres de formation, plusieurs clients nous confient la gestion de leurs appels et de leur support au quotidien.
            </p>
            <p className="text-lg font-semibold text-primary mt-4">Vous êtes bien entouré.</p>
            <p className="text-gray-600 mt-2">Ne nous croyez pas sur parole :</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <div className="text-4xl mb-4">💬</div>
              <p className="text-gray-700 mb-4 italic">
                "ZiExter est devenu un vrai prolongement de notre équipe. Ils comprennent nos process et forment leurs agents avec rigueur et réactivité."
              </p>
              <div className="font-semibold">Liliane Rhebert</div>
              <div className="text-sm text-gray-600">Responsable E-commerce – Maison du Café</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <div className="text-4xl mb-4">💬</div>
              <p className="text-gray-700 mb-4 italic">
                "Nous avons désormais une base de connaissances claire et efficace. Les agents ZiExter trouvent rapidement les réponses et gagnent en productivité chaque jour."
              </p>
              <div className="font-semibold">Julien Pontils</div>
              <div className="text-sm text-gray-600">Customer Success Manager – StartAuto</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <div className="text-4xl mb-4">💬</div>
              <p className="text-gray-700 mb-4 italic">
                "Contrairement à d'autres prestataires, ZiExter s'implique dans toute la chaîne : qualité, formation, suivi et recrutement. Résultat : plus d'efficacité et moins de frictions."
              </p>
              <div className="font-semibold">Camille Rehawi</div>
              <div className="text-sm text-gray-600">Responsable des Opérations – ÉnergiePlus</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              📌 Vous êtes à 1 minute de votre meilleure expérience client.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/rendez-vous" className="btn btn-accent btn-lg">
                👉 Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceClient;
